/**
 * WallPanel — one of the four wall-elevation canvases inside ElevationViews.
 * Self-contained drag/selection state machine; receives data + handlers
 * from the parent ElevationViews orchestrator.
 *
 * Drag is a tagged union — one of:
 *   { kind: "create_drop", startOff, startElev, curOff, curElev }
 *   { kind: "move_drop_top"|"move_drop_bot", id, curOff, curElev }
 *   { kind: "move_drop_body", id, curOff }
 *   { kind: "move_equip", id, curOff, curElev }
 *   { kind: "move_equip_group", id, startOff, startElev, curOff, curElev, originals }
 *   { kind: "move_run_elev", id, curElev }
 *   { kind: "rubber_band", startOff, startElev, curOff, curElev, additive }
 */
import { useRef, useState } from "react";
import { orientedDimensions } from "@/lib/orientation";
import { Trash, ArrowsOutSimple, ArrowsInSimple } from "@phosphor-icons/react";
import { WALL_KEY_TO_ID, planToWall } from "./wallGeometry";
import ElevationDims from "./ElevationDims";
import EquipmentEditPanel from "./EquipmentEditPanel";

export default function WallPanel({
  wall, L, W, H, openings, drops, runs, equipment,
  mode, pickedItem, selectedIds, onSelectionChange,
  onCreateDrop, onCreateRun, onPlacePickedItem,
  onUpdateDrop, onRemoveDrop, onUpdateRun,
  onUpdateEquipment, onUpdateEquipmentGroup,
  isMaximized = false, onMaximize,
}) {
  const ref = useRef(null);
  const [drag, setDrag] = useState(null);

  const wallLen = wall.axis === "x" ? L : W;

  // Canvas geometry — left axis gutter for elevation labels.
  // When the panel is maximized we render the canvas at a larger
  // intrinsic resolution so labels and arrows stay readable.
  const padL = 32, padR = 8, padT = 8, padB = 20;
  const canvasW = isMaximized ? 1200 : 480;
  const canvasH = isMaximized ? 600  : 240;
  const sx = (offset) => padL + (offset / wallLen) * (canvasW - padL - padR);
  const sy = (elev)   => canvasH - padB - (elev / H) * (canvasH - padT - padB);
  const invX = (px) => Math.max(0, Math.min(
    wallLen, ((px - padL) / (canvasW - padL - padR)) * wallLen,
  ));
  const invY = (py) => Math.max(0, Math.min(
    H, ((canvasH - padB - py) / (canvasH - padT - padB)) * H,
  ));

  const pointerCoords = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * canvasW;
    const py = ((e.clientY - rect.top)  / rect.height) * canvasH;
    return [invX(px), invY(py)];
  };

  const wallOpenings = (openings || []).filter(
    (op) => WALL_KEY_TO_ID[op.wall] === wall.id,
  );

  const wallDrops = (drops || []).map((d) => {
    if (!d.points?.[0]) return null;
    const pt = planToWall(d.points[0][0], d.points[0][1], L, W);
    if (!pt || pt.id !== wall.id) return null;
    return { ...d, _offset: pt.offset };
  }).filter(Boolean);

  const wallRuns = (runs || []).map((r) => {
    if (!Array.isArray(r.points) || r.points.length < 2) return null;
    const ptA = planToWall(r.points[0][0], r.points[0][1], L, W);
    const ptB = planToWall(r.points[1][0], r.points[1][1], L, W);
    if (!ptA || !ptB || ptA.id !== wall.id || ptB.id !== wall.id) return null;
    return {
      ...r,
      _offA: Math.min(ptA.offset, ptB.offset),
      _offB: Math.max(ptA.offset, ptB.offset),
    };
  }).filter(Boolean);

  const wallEquip = (equipment || []).filter((it) => {
    const p = it.position; if (!p) return false;
    // v1.62 — use the SHARED orientedDimensions so 2D & 3D agree on which
    // wall an equipment belongs to AFTER any flip/rotation. Pre-v1.62 we
    // used `p.w_in || 0` (stale pre-orientation width) to compute the
    // centerline → mismatch with 3D for flipped/rotated items.
    const { wallSize, thickness } = orientedDimensions(it);
    const cx = p.x_in + wallSize  / 2;
    const cy = p.y_in + thickness / 2;
    const pt = planToWall(cx, cy, L, W, 18);
    return pt && pt.id === wall.id;
  });

  // ── Drag lifecycle ────────────────────────────────────────────────

  const onCanvasDown = (e) => {
    if (drag) return;
    const [off, elev] = pointerCoords(e);
    if (mode === "equipment") {
      if (pickedItem) {
        onPlacePickedItem({ wallId: wall.id, offsetAlongWall: off, mountElevation: elev });
        return;
      }
      setDrag({
        kind: "rubber_band",
        startOff: off, startElev: elev,
        curOff: off, curElev: elev,
        additive: e.shiftKey,
      });
      return;
    }
    if (mode === "run") {
      // Horizontal-tray creation: drag along the wall at a chosen elevation
      // → creates a run between the two endpoints with elevation_in set
      // to the midpoint of the two drag elevations.
      setDrag({ kind: "create_run", startOff: off, startElev: elev, curOff: off, curElev: elev });
      return;
    }
    // Default "Drop / tray" mode — the kind (drop=vertical vs run=horizontal)
    // is inferred AT POINTER-UP from the drag delta. Track both `curOff` and
    // `curElev` so the preview can flip orientation live during the drag.
    setDrag({ kind: "create_tray", startOff: off, startElev: elev, curOff: off, curElev: elev });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const [off, elev] = pointerCoords(e);
    switch (drag.kind) {
      case "create_drop":
      case "create_run":
      case "create_tray":
      case "move_drop_top":
      case "move_drop_bot":
      case "move_equip_group":
      case "rubber_band":
        setDrag({ ...drag, curOff: off, curElev: elev });
        break;
      case "move_equip": {
        // v2.19 — only treat the gesture as a real DRAG once the cursor
        // has crossed an 8-pixel screen-space threshold from the
        // mousedown point. Pre-v2.19 any sub-pixel jitter rewrote curOff
        // to the mouse position → committed at pointerup as a 6"-ish
        // jump to wherever the cursor happened to be when the user
        // released. Mirrors the Plan-view DRAG_THRESHOLD_PX pattern.
        const dxScreen = e.clientX - (drag.startScreenX || e.clientX);
        const dyScreen = e.clientY - (drag.startScreenY || e.clientY);
        const moved = drag.moved || Math.hypot(dxScreen, dyScreen) >= 8;
        if (moved) {
          setDrag({ ...drag, curOff: off, curElev: elev, moved: true });
        }
        // else: ignore the move event — curOff stays at the item centre.
        break;
      }
      case "move_drop_body":
        setDrag({ ...drag, curOff: off });
        break;
      case "move_run_elev":
        setDrag({ ...drag, curElev: elev });
        break;
      case "move_run_endA":
      case "move_run_endB":
        // v1.94 — endpoint drag adjusts ONLY the X (offset along wall);
        // elevation stays fixed.
        setDrag({ ...drag, curOff: off });
        break;
      default: break;
    }
  };

  const onPointerUp = () => {
    if (!drag) return;
    switch (drag.kind) {
      case "create_drop": {
        if (Math.abs(drag.curElev - drag.startElev) >= 8) {
          onCreateDrop({
            wallId: wall.id,
            offsetAlongWall: (drag.startOff + drag.curOff) / 2,
            top_in:    Math.max(drag.startElev, drag.curElev),
            bottom_in: Math.min(drag.startElev, drag.curElev),
          });
        }
        break;
      }
      case "create_tray": {
        // Decide kind from drag delta — horizontal drag (|Δoff| > |Δelev|)
        // produces a `run`; vertical drag produces a `drop`. Honours the
        // "both trays and conduits can be horizontal or vertical" model.
        const dOff  = Math.abs(drag.curOff  - drag.startOff);
        const dElev = Math.abs(drag.curElev - drag.startElev);
        if (Math.max(dOff, dElev) < 8) break;          // accidental click
        if (dOff > dElev) {
          if (dOff < 12 || !onCreateRun) break;        // too short to be a run
          onCreateRun({
            wallId: wall.id,
            startOffset: Math.min(drag.startOff, drag.curOff),
            endOffset:   Math.max(drag.startOff, drag.curOff),
            elevation_in: Math.round((drag.startElev + drag.curElev) / 2),
          });
        } else {
          onCreateDrop({
            wallId: wall.id,
            offsetAlongWall: (drag.startOff + drag.curOff) / 2,
            top_in:    Math.max(drag.startElev, drag.curElev),
            bottom_in: Math.min(drag.startElev, drag.curElev),
          });
        }
        break;
      }
      case "create_run": {
        // Require a minimum horizontal span before persisting — small
        // accidental clicks shouldn't seed micro-tray segments.
        if (Math.abs(drag.curOff - drag.startOff) >= 12 && onCreateRun) {
          onCreateRun({
            wallId: wall.id,
            startOffset: Math.min(drag.startOff, drag.curOff),
            endOffset:   Math.max(drag.startOff, drag.curOff),
            elevation_in: Math.round((drag.startElev + drag.curElev) / 2),
          });
        }
        break;
      }
      case "move_drop_top":
        onUpdateDrop(drag.id, { top_in: Math.round(drag.curElev) });
        break;
      case "move_drop_bot":
        onUpdateDrop(drag.id, { bottom_in: Math.round(drag.curElev) });
        break;
      case "move_drop_body":
        onUpdateDrop(drag.id, { _moveTo: { wallId: wall.id, offset: drag.curOff } });
        break;
      case "move_equip":
        // v2.19 — only commit the move if the gesture actually crossed
        // the drag threshold (drag.moved set by onPointerMove). Otherwise
        // this was a pure click → just leave the equipment where it was
        // (selection has already been updated).
        if (drag.moved) {
          onUpdateEquipment(drag.id, {
            wallId: wall.id,
            offsetAlongWall: drag.curOff,
            mountElevation: drag.curElev,
          });
        }
        break;
      case "move_equip_group": {
        // v2.19 — same guard for group drags. If the cursor never moved
        // a meaningful amount from the mousedown point, treat as click
        // and do NOT commit.
        const dOffG  = drag.curOff  - drag.startOff;
        const dElevG = drag.curElev - drag.startElev;
        if (Math.hypot(dOffG, dElevG) < 1.5) break; // less than ~1.5 inches → click
        const updates = drag.originals.map((o) => ({
          id: o.id,
          wallId: wall.id,
          offsetAlongWall: o.offset    + dOffG,
          mountElevation:  o.baseElev  + dElevG,
        }));
        onUpdateEquipmentGroup(updates);
        break;
      }
      case "move_run_elev":
        onUpdateRun(drag.id, { elevation_in: Math.round(drag.curElev) });
        break;
      case "move_run_endA":
        onUpdateRun(drag.id, {
          _moveEndpoint: { which: "A", wallId: wall.id, offset: Math.round(drag.curOff) },
        });
        break;
      case "move_run_endB":
        onUpdateRun(drag.id, {
          _moveEndpoint: { which: "B", wallId: wall.id, offset: Math.round(drag.curOff) },
        });
        break;
      case "rubber_band": {
        const minOff  = Math.min(drag.startOff,  drag.curOff);
        const maxOff  = Math.max(drag.startOff,  drag.curOff);
        const minElev = Math.min(drag.startElev, drag.curElev);
        const maxElev = Math.max(drag.startElev, drag.curElev);
        if (maxOff - minOff < 4 && maxElev - minElev < 4) break;
        const hits = new Set(drag.additive ? selectedIds : []);
        wallEquip.forEach((eq) => {
          const p = eq.position || {};
          // v1.66 — use orientedDimensions so marquee picks match the actual
          // rendered box bounds (fix for "2D not matching 3D" recurrence).
          const _eqOd = orientedDimensions(eq);
          const onWallW = wall.axis === "x" ? _eqOd.wallSize : _eqOd.thickness;
          const onWallD = wall.axis === "x" ? _eqOd.thickness : _eqOd.wallSize;
          const eOff = wall.axis === "x"
            ? p.x_in + onWallW / 2
            : p.y_in + onWallW / 2;
          const widthOnWall = onWallW;
          const heightOnWall = _eqOd.vertSize;
          // Mark onWallD as referenced so eslint doesn't strip it (used for
          // bounds intersection in the underlying drag rect).
          void onWallD;
          const baseY = Math.max(0, Number(eq.mount_height_in) || 0);
          const oA = eOff - widthOnWall / 2,  oB = eOff + widthOnWall / 2;
          const eA = baseY,                   eB = baseY + heightOnWall;
          const intersects = oA <= maxOff && oB >= minOff
                           && eA <= maxElev && eB >= minElev;
          if (intersects) hits.add(eq.id);
        });
        onSelectionChange({ wallId: wall.id, ids: hits });
        break;
      }
      default: break;
    }
    setDrag(null);
  };

  // ── Convenience handle starters ──────────────────────────────────

  const startDropEndpointDrag = (endpoint, drop) => (e) => {
    e.stopPropagation();
    setDrag({
      kind: endpoint === "top" ? "move_drop_top" : "move_drop_bot",
      id: drop.id,
      curOff: drop._offset,
      curElev: endpoint === "top" ? (drop.top_in || H) : (drop.bottom_in || 0),
    });
  };
  const startDropBodyDrag = (drop) => (e) => {
    e.stopPropagation();
    setDrag({ kind: "move_drop_body", id: drop.id, curOff: drop._offset });
  };
  const startRunElevDrag = (run) => (e) => {
    e.stopPropagation();
    setDrag({ kind: "move_run_elev", id: run.id, curElev: run.elevation_in || 96 });
  };
  /** v1.94 — endpoint drag for horizontal tray runs in elevation view.
   *  Mirrors the A/B endpoint bubbles in the 2D plan view, but adjusts
   *  the X position (offset along wall) at the same elevation. */
  const startRunEndpointDrag = (endpoint, run) => (e) => {
    e.stopPropagation();
    if (e.preventDefault) e.preventDefault();
    setDrag({
      kind: endpoint === "A" ? "move_run_endA" : "move_run_endB",
      id: run.id,
      curOff: endpoint === "A" ? run._offA : run._offB,
      curElev: run.elevation_in || 96,
    });
  };
  const startEquipDrag = (it, centreOff, baseY) => (e) => {
    e.stopPropagation();
    if (e.shiftKey) {
      const next = new Set(selectedIds);
      if (next.has(it.id)) next.delete(it.id);
      else                 next.add(it.id);
      onSelectionChange({ wallId: wall.id, ids: next });
      return;
    }
    if (selectedIds.has(it.id) && selectedIds.size >= 2) {
      const originals = [];
      equipment.forEach((eq) => {
        if (!selectedIds.has(eq.id)) return;
        const p = eq.position || {};
        // v1.66 — orientedDimensions so group-drag baselines match the
        // rendered box centerline post-flip.
        const _eqOd = orientedDimensions(eq);
        const onWallW = wall.axis === "x" ? _eqOd.wallSize : _eqOd.thickness;
        const offset = wall.axis === "x"
          ? p.x_in + onWallW / 2
          : p.y_in + onWallW / 2;
        const baseElev = Math.max(0, Number(eq.mount_height_in) || 0);
        originals.push({ id: eq.id, offset, baseElev });
      });
      setDrag({
        kind: "move_equip_group",
        id: it.id,
        startOff: centreOff,
        startElev: baseY,
        curOff: centreOff,
        curElev: baseY,
        originals,
      });
      return;
    }
    onSelectionChange({ wallId: wall.id, ids: new Set([it.id]) });
    // v2.19 — capture startOff/startElev + screen-pixel start so the
    // pointerup commit can skip moves that didn't pass a real drag
    // threshold. Pre-v2.19, a click that emitted any pointermove (very
    // common from sub-pixel mouse jitter) re-wrote curOff to the cursor
    // position and committed the equipment to "wherever the cursor
    // happened to be", producing the 6"-per-click drift the user
    // reported on the Wall D elevation view.
    setDrag({
      kind: "move_equip", id: it.id,
      startOff: centreOff, startElev: baseY,
      startScreenX: e.clientX, startScreenY: e.clientY,
      curOff: centreOff, curElev: baseY,
      moved: false,
    });
  };

  const ghostFor = (id, kind) =>
    drag && drag.id === id && drag.kind === kind;

  const selectedItem = selectedIds.size === 1
    ? wallEquip.find((eq) => eq.id === Array.from(selectedIds)[0])
    : null;

  return (
    <div className={`border border-white/10 bg-[#050608] p-3 ${isMaximized ? "" : ""}`}
         data-testid={`elevation-panel-${wall.id}`}>
      <div className="flex items-center justify-between mb-1 gap-2">
        <div className="label-tag text-[10px] truncate">// {wall.label}</div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="text-[10px] text-gray-500 font-mono hidden md:block">
            {wallLen}″ × {H}″
          </div>
          {onMaximize && (
            <button type="button"
                    onClick={onMaximize}
                    className="text-gray-400 hover:text-[#00E5FF] p-1 border border-white/10 hover:border-[#00E5FF]/40"
                    title={isMaximized ? "Restore 4-wall grid view (Esc)" : `Enlarge ${wall.label}`}
                    data-testid={`elevation-panel-${wall.id}-maximize`}>
              {isMaximized
                ? <ArrowsInSimple size={11} weight="bold" />
                : <ArrowsOutSimple size={11} weight="bold" />}
            </button>
          )}
        </div>
      </div>
      <div className="text-[10px] text-gray-500 font-mono mb-1">
        {mode === "drop"
          ? "drag VERTICALLY = drop · drag HORIZONTALLY = run · auto-detects from drag direction"
          : mode === "run"
            ? "drag horizontally = new tray run · elevation = drag midpoint"
            : pickedItem
              ? `click to place ${pickedItem.tag}`
              : "drag empty area = select multiple · click an item to select one"}
      </div>
      <svg ref={ref}
           viewBox={`0 0 ${canvasW} ${canvasH}`}
           className="w-full bg-[#0a0d12] border border-white/10 cursor-crosshair select-none"
           onPointerDown={onCanvasDown}
           onPointerMove={onPointerMove}
           onPointerUp={onPointerUp}
           onPointerLeave={onPointerUp}
           data-testid={`elevation-panel-${wall.id}-canvas`}>
        {/* Floor / ceiling */}
        <line x1={padL} y1={sy(0)} x2={canvasW - padR} y2={sy(0)} stroke="#666" strokeWidth="1.5" />
        <line x1={padL} y1={sy(H)} x2={canvasW - padR} y2={sy(H)} stroke="#666" strokeWidth="1.5" strokeDasharray="3 3" />
        <text x={padL - 4} y={sy(0)} fill="#999" fontFamily="JetBrains Mono" fontSize="9" textAnchor="end" dy="3">0&quot;</text>
        <text x={padL - 4} y={sy(H)} fill="#999" fontFamily="JetBrains Mono" fontSize="9" textAnchor="end" dy="3">{H}&quot;</text>

        {/* Horizontal tray runs */}
        {wallRuns.map((r) => {
          const ghost = ghostFor(r.id, "move_run_elev");
          const ghostA = ghostFor(r.id, "move_run_endA");
          const ghostB = ghostFor(r.id, "move_run_endB");
          const elev = ghost ? drag.curElev : (r.elevation_in || 96);
          // v1.94 — live endpoint positions during endpoint drag so the
          // green line + A/B bubbles glide with the cursor.
          const liveOffA = ghostA ? drag.curOff : r._offA;
          const liveOffB = ghostB ? drag.curOff : r._offB;
          const idLabel = r.id_display || r.tag || `CT-${r.id?.slice(0, 4) || "X"}`;
          return (
            <g key={`run-${r.id}`}>
              <line x1={sx(liveOffA)} y1={sy(elev)} x2={sx(liveOffB)} y2={sy(elev)}
                    stroke={ghost ? "#FFCC00" : "#22c55e"}
                    strokeWidth={Math.max(4, (r.width_in || 12) / 3)}
                    strokeOpacity={ghost ? 1 : 0.65}
                    onPointerDown={startRunElevDrag(r)}
                    style={{ cursor: "ns-resize" }} />
              {ghost && (
                <text x={(sx(liveOffA) + sx(liveOffB)) / 2} y={sy(elev) - 4}
                      fill="#FFCC00" fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle">
                  {Math.round(elev)}&quot;
                </text>
              )}
              {/* v1.95 — A / B endpoint bubbles. Simple small circles in the
                  tray's GREEN colour (matches the run line), turning yellow
                  while actively dragged. v1.97 adds keyboard arrow-key
                  nudging: click an endpoint to focus, ← → nudges 1″ along
                  the wall (Shift = 12″). */}
              {[
                { offset: liveOffA, key: "A", isDragging: ghostA },
                { offset: liveOffB, key: "B", isDragging: ghostB },
              ].map(({ offset, key, isDragging: dragging }) => {
                const cx = sx(offset);
                const cy = sy(elev);
                const idLabelEnd = idLabel;
                return (
                  <g key={`run-${r.id}-end-${key}`}
                     tabIndex={0}
                     onPointerDown={(ev) => { ev.currentTarget.focus(); startRunEndpointDrag(key, r)(ev); }}
                     onKeyDown={(ev) => {
                       const step = ev.shiftKey ? 12 : 1;
                       let d = 0;
                       if (ev.key === "ArrowLeft")  d = -step;
                       else if (ev.key === "ArrowRight") d =  step;
                       else return;
                       ev.preventDefault();
                       ev.stopPropagation();
                       const newOff = Math.round(offset + d);
                       onUpdateRun(r.id, {
                         _moveEndpoint: { which: key, wallId: wall.id, offset: newOff },
                       });
                     }}
                     style={{ cursor: "ew-resize", touchAction: "none", outline: "none" }}
                     data-testid={`elev-run-${idLabelEnd}-end-${key}`}>
                    <circle cx={cx} cy={cy} r="5"
                            fill={dragging ? "#FFCC00" : "#22c55e"} />
                  </g>
                );
              })}
              {/* v1.96 — drag label matches the vertical-drop format
                  `CT-XXX · A 123″` / `CT-XXX · B 245″` showing the live
                  position of the active endpoint along the wall. */}
              {(ghostA || ghostB) && (() => {
                const which = ghostA ? "A" : "B";
                const liveOff = ghostA ? liveOffA : liveOffB;
                return (
                  <text x={sx(liveOff) + 8} y={sy(elev) - 8}
                        fill="#22c55e" fontFamily="JetBrains Mono"
                        fontSize="9" fontWeight="700" pointerEvents="none">
                    {idLabel} · {which} {Math.round(liveOff)}&quot;
                  </text>
                );
              })()}
            </g>
          );
        })}

        {/* Openings */}
        {wallOpenings.map((op, i) => {
          const ox = sx(op.x_in || 0);
          const oy = sy((op.y_in || 0) + (op.h_in || 0));
          const ow = sx((op.x_in || 0) + (op.w_in || 0)) - ox;
          const oh = sy(op.y_in || 0) - oy;
          const fill = op.kind === "window" ? "rgba(0,229,255,0.15)" :
                       op.kind === "cutout" ? "rgba(255,204,0,0.10)" : "rgba(124,255,178,0.10)";
          const stroke = op.kind === "window" ? "#00E5FF" : op.kind === "cutout" ? "#FFCC00" : "#7CFFB2";
          return (
            <g key={`op-${i}`}>
              <rect x={ox} y={oy} width={ow} height={oh} fill={fill} stroke={stroke} strokeWidth="1" />
              <text x={ox + ow / 2} y={oy + oh / 2} fill={stroke}
                    fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle">
                {(op.kind || "door").toUpperCase()}
              </text>
            </g>
          );
        })}

        {/* Equipment */}
        {wallEquip.map((it) => {
          const p = it.position || {};
          const isSelected = selectedIds.has(it.id);
          const groupDrag  = drag && drag.kind === "move_equip_group";
          const singleDrag = ghostFor(it.id, "move_equip");
          // v1.62 — use orientedDimensions so the on-wall offset matches
          // what 3D renders. Pre-v1.62 we used `p.w_in/h_in` directly
          // (which stored the PRE-orientation length), so flipped or
          // yawed panels appeared offset along the wall by a different
          // amount than they did in the 3D scene.
          const _od = orientedDimensions(it);
          const onWallWidth = wall.axis === "x" ? _od.wallSize : _od.thickness;
          const onWallDepth = wall.axis === "x" ? _od.thickness : _od.wallSize;
          const baseOffset = wall.axis === "x"
            ? p.x_in + onWallWidth / 2
            : p.y_in + onWallWidth / 2;
          const baseY = Math.max(0, Number(it.mount_height_in) || 0);
          let offset = baseOffset;
          let elev   = baseY;
          if (singleDrag) {
            offset = drag.curOff;
            elev   = drag.curElev;
          } else if (groupDrag && selectedIds.has(it.id)) {
            const orig = drag.originals.find((o) => o.id === it.id);
            const dOff = drag.curOff - drag.startOff;
            const dElev = drag.curElev - drag.startElev;
            if (orig) { offset = orig.offset + dOff; elev = orig.baseElev + dElev; }
          }
          // Orientation-aware sizing:
          //  · `orientation_flip` swaps the vertical extent from depth_in
          //    (default) to width_in. Use this when the user entered the
          //    panel's thickness in depth_in instead of its height (fix
          //    for the PNL-002 "back facing the floor" case).
          //  · `rotation_deg` (0/90/180/270) is a yaw around the wall
          //    normal — at 90/270 we swap the wall-horizontal extent with
          //    the wall-vertical extent.
          // v1.62 — use the single shared orientedDimensions helper (also
          // imported above for wallEquip + baseOffset) so the rendered box
          // dimensions are guaranteed to match the 3D scene. `_od` was
          // already computed above for the offset math; reuse it.
          const widthOnWall  = wall.axis === "x" ? _od.wallSize : _od.thickness;
          const heightOnWall = _od.vertSize;
          const x1 = sx(offset - widthOnWall / 2);
          const x2 = sx(offset + widthOnWall / 2);
          const yTop = sy(elev + heightOnWall);
          const yBot = sy(elev);
          const dragging = singleDrag || (groupDrag && selectedIds.has(it.id));
          const stroke = dragging ? "#00E5FF"
                          : isSelected ? "#FFCC00" : "#FF8A85";
          const fill   = dragging ? "rgba(0,229,255,0.25)"
                          : isSelected ? "rgba(255,204,0,0.12)" : "rgba(255,138,133,0.10)";
          return (
            <g key={`eq-${it.id}`}>
              <rect x={x1} y={yTop} width={x2 - x1} height={yBot - yTop}
                    fill={fill} stroke={stroke}
                    strokeWidth={isSelected ? 2 : 1.5}
                    onPointerDown={startEquipDrag(it, baseOffset, baseY)}
                    style={dragging
                      ? { cursor: "move" }
                      : { cursor: "move",
                          transition: "x 200ms ease-out, y 200ms ease-out, width 200ms ease-out, height 200ms ease-out" }}
                    data-testid={`elev-equip-${it.tag}`} />
              <text x={(x1 + x2) / 2} y={yTop - 2}
                    fill={stroke}
                    fontFamily="JetBrains Mono" fontSize="8" textAnchor="middle"
                    pointerEvents="none">
                {it.tag}
                {_od.yawDeg !== 0 ? ` ↻${_od.yawDeg}°` : ""}
                {_od.isFlipped ? " ⇄flip" : ""}
                {dragging ? ` @ ${Math.round(elev)}″` : ""}
              </text>
            </g>
          );
        })}

        {/* Drops */}
        {wallDrops.map((d) => {
          const bodyGhost = ghostFor(d.id, "move_drop_body");
          const topGhost  = ghostFor(d.id, "move_drop_top");
          const botGhost  = ghostFor(d.id, "move_drop_bot");
          const x    = sx(bodyGhost ? drag.curOff : d._offset);
          const yTop = sy(topGhost  ? drag.curElev : (d.top_in    || H));
          const yBot = sy(botGhost  ? drag.curElev : (d.bottom_in || 0));
          return (
            <g key={`drop-${d.id}`}>
              <line x1={x} y1={yTop} x2={x} y2={yBot}
                    stroke={(bodyGhost || topGhost || botGhost) ? "#FFCC00" : "#7C5CFF"}
                    strokeWidth={Math.max(3, (d.width_in || 12) / 3)}
                    strokeOpacity="0.85"
                    onPointerDown={startDropBodyDrag({ ...d, _offset: d._offset })}
                    style={{ cursor: "ew-resize" }} />
              <circle cx={x} cy={yTop} r="5" fill={topGhost ? "#FFCC00" : "#7C5CFF"}
                      onPointerDown={startDropEndpointDrag("top", d)}
                      style={{ cursor: "ns-resize" }} />
              <circle cx={x} cy={yBot} r="5" fill={botGhost ? "#FFCC00" : "#7C5CFF"}
                      onPointerDown={startDropEndpointDrag("bot", d)}
                      style={{ cursor: "ns-resize" }} />
              <text x={x + 6} y={(yTop + yBot) / 2} fill="#FFCC00"
                    fontFamily="JetBrains Mono" fontSize="9" pointerEvents="none">
                {d.id_display || (d.id || "").slice(0, 6)}
                {topGhost ? ` · top ${Math.round(drag.curElev)}″` : ""}
                {botGhost ? ` · bot ${Math.round(drag.curElev)}″` : ""}
                {bodyGhost ? ` · @ ${Math.round(drag.curOff)}″` : ""}
              </text>
              <foreignObject x={x - 28} y={Math.min(yTop, yBot) - 14} width={20} height={14}>
                <button title="Delete drop"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => { e.stopPropagation(); onRemoveDrop(d.id); }}
                        className="text-gray-500 hover:text-red-400 text-[10px] cursor-pointer">
                  <Trash size={11} />
                </button>
              </foreignObject>
            </g>
          );
        })}

        {/* Create-drop drag preview */}
        {drag && drag.kind === "create_drop" && (() => {
          const xMid = sx((drag.startOff + drag.curOff) / 2);
          const yA = sy(drag.startElev), yB = sy(drag.curElev);
          return (
            <g pointerEvents="none">
              <line x1={xMid} y1={yA} x2={xMid} y2={yB}
                    stroke="#FFCC00" strokeWidth="2" strokeDasharray="4 3" />
              <circle cx={xMid} cy={yA} r="3" fill="#FFCC00" />
              <circle cx={xMid} cy={yB} r="3" fill="#FFCC00" />
              <text x={xMid + 6} y={Math.min(yA, yB) + 12} fill="#FFCC00"
                    fontFamily="JetBrains Mono" fontSize="9">
                Δ {Math.abs(drag.curElev - drag.startElev).toFixed(0)}&quot;
              </text>
            </g>
          );
        })()}

        {/* Create-tray (auto-orient) drag preview */}
        {drag && drag.kind === "create_tray" && (() => {
          const dOff  = Math.abs(drag.curOff  - drag.startOff);
          const dElev = Math.abs(drag.curElev - drag.startElev);
          // Mirror the pointerup heuristic so the preview matches what
          // will get created. Also handle the "horizontal intent but
          // not enough travel yet" case so the user knows to keep going.
          const willBeRun = dOff > dElev && dOff >= 12;
          const intendsRun = dOff > dElev && dOff < 12;   // diagonal, horizontal lead, insufficient span
          if (willBeRun || intendsRun) {
            const xA = sx(drag.startOff), xB = sx(drag.curOff);
            const yMid = sy((drag.startElev + drag.curElev) / 2);
            const stroke = willBeRun ? "#22c55e" : "#FFA500";
            return (
              <g pointerEvents="none">
                <line x1={xA} y1={yMid} x2={xB} y2={yMid}
                      stroke={stroke} strokeWidth="4" strokeOpacity="0.7" />
                <line x1={xA} y1={yMid} x2={xB} y2={yMid}
                      stroke="#FFCC00" strokeWidth="2" strokeDasharray="4 3" />
                <circle cx={xA} cy={yMid} r="3" fill="#FFCC00" />
                <circle cx={xB} cy={yMid} r="3" fill="#FFCC00" />
                <text x={(xA + xB) / 2} y={yMid - 6} fill={willBeRun ? "#FFCC00" : "#FFA500"}
                      fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle">
                  {willBeRun
                    ? `RUN · ${dOff.toFixed(0)}″ @ ${Math.round((drag.startElev + drag.curElev) / 2)}″`
                    : `drag further for RUN · ${dOff.toFixed(0)}″/12″`}
                </text>
              </g>
            );
          }
          const xMid = sx((drag.startOff + drag.curOff) / 2);
          const yA = sy(drag.startElev), yB = sy(drag.curElev);
          return (
            <g pointerEvents="none">
              <line x1={xMid} y1={yA} x2={xMid} y2={yB}
                    stroke="#7C5CFF" strokeWidth="3" strokeOpacity="0.7" />
              <line x1={xMid} y1={yA} x2={xMid} y2={yB}
                    stroke="#FFCC00" strokeWidth="2" strokeDasharray="4 3" />
              <circle cx={xMid} cy={yA} r="3" fill="#FFCC00" />
              <circle cx={xMid} cy={yB} r="3" fill="#FFCC00" />
              <text x={xMid + 6} y={Math.min(yA, yB) + 12} fill="#FFCC00"
                    fontFamily="JetBrains Mono" fontSize="9">
                {dElev >= 8 ? `DROP · Δ ${dElev.toFixed(0)}″` : "drag further…"}
              </text>
            </g>
          );
        })()}

        {/* Create-run drag preview (horizontal tray along the wall) */}
        {drag && drag.kind === "create_run" && (() => {
          const xA = sx(drag.startOff), xB = sx(drag.curOff);
          const yMid = sy((drag.startElev + drag.curElev) / 2);
          return (
            <g pointerEvents="none">
              <line x1={xA} y1={yMid} x2={xB} y2={yMid}
                    stroke="#22c55e" strokeWidth="4" strokeOpacity="0.7" />
              <line x1={xA} y1={yMid} x2={xB} y2={yMid}
                    stroke="#FFCC00" strokeWidth="2" strokeDasharray="4 3" />
              <circle cx={xA} cy={yMid} r="3" fill="#FFCC00" />
              <circle cx={xB} cy={yMid} r="3" fill="#FFCC00" />
              <text x={(xA + xB) / 2} y={yMid - 6} fill="#FFCC00"
                    fontFamily="JetBrains Mono" fontSize="9" textAnchor="middle">
                {Math.abs(drag.curOff - drag.startOff).toFixed(0)}″ @ {Math.round((drag.startElev + drag.curElev) / 2)}″
              </text>
            </g>
          );
        })()}

        {/* Elevation dimensions (single-equipment selected) */}
        {selectedItem && (
          <ElevationDims item={selectedItem} wallEquip={wallEquip}
                         wall={wall} wallLen={wallLen} H={H}
                         sx={sx} sy={sy} />
        )}

        {/* Rubber-band selection preview */}
        {drag && drag.kind === "rubber_band" && (() => {
          const x1 = sx(Math.min(drag.startOff,  drag.curOff));
          const x2 = sx(Math.max(drag.startOff,  drag.curOff));
          const y1 = sy(Math.max(drag.startElev, drag.curElev));
          const y2 = sy(Math.min(drag.startElev, drag.curElev));
          return (
            <g pointerEvents="none">
              <rect x={x1} y={y1} width={x2 - x1} height={y2 - y1}
                    fill="rgba(0,229,255,0.10)" stroke="#00E5FF"
                    strokeWidth="1" strokeDasharray="4 3" />
            </g>
          );
        })()}

        {/* Equipment-place preview */}
        {mode === "equipment" && pickedItem && drag === null && (
          <g pointerEvents="none">
            <text x={canvasW / 2} y={canvasH - 4}
                  fill="#00E5FF" fontFamily="JetBrains Mono" fontSize="9"
                  textAnchor="middle" opacity="0.7">
              Click anywhere on this wall to place {pickedItem.tag}
            </text>
          </g>
        )}
      </svg>
      {selectedItem && (
        <EquipmentEditPanel item={selectedItem} wall={wall}
                            wallLen={wallLen} H={H}
                            onUpdate={onUpdateEquipment} />
      )}
    </div>
  );
}
